import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import bankIcon from '../../../../assets/reimbursement-method/bank.svg';
import checkIcon from '../../../../assets/reimbursement-method/check.svg';
import checkGreen from '../../../../assets/reimbursement-method/check-green.svg';
import starBadge from '../../../../assets/reimbursement-method/star.svg';
import careCreditIcon
  from '../../../../assets/reimbursement-method/care-credit.svg';

import { RadioButton } from '../../../common/RadioButton';
import { REIMBURSEMENT_METHODS } from '../../../../constants';

import styles from './ReimbursementMethodOption.module.css';

/**
 * Renders a single reimbursement method choice, either as the legacy
 * radio-button row or as the redesigned selectable card, based on the
 * `newDesign` flag (driven by the useReimbursementMethodDisclaimer
 * feature flag). Both designs share the same method metadata below,
 * so copy/icons only need to be defined once per method.
 */
const ReimbursementMethodOption = ({
  checked = false,
  legacyClassName = '',
  method,
  newDesign = false,
  onChange = () => { },
}) => {
  const { t } = useTranslation('myAccount');

  const methodConfig = useMemo(() => ({
    [REIMBURSEMENT_METHODS.deposit]: {
      content: (
        <div className={styles.bulletRow}>
          <span className={styles.bullet}>
            <img alt="" src={checkGreen} />

            {t('reimbursementSection.editModal.directDepositFundsTime')}
          </span>

          <span className={styles.bullet}>
            <img alt="" src={checkGreen} />

            {t('reimbursementSection.editModal.directDepositAutomatic')}
          </span>
        </div>
      ),
      icon: bankIcon,
      iconBadge: starBadge,
      id: 'reimbursement-direct-deposit',
      recommended: true,
      title: t('reimbursementSection.editModal.directDeposit'),
    },
    [REIMBURSEMENT_METHODS.check]: {
      content: (
        <span className={styles.tag}>
          {t('reimbursementSection.editModal.checkDeliveryTime')}
        </span>
      ),
      icon: checkIcon,
      id: 'reimbursement-check',
      title: t('reimbursementSection.editModal.check'),
    },
    [REIMBURSEMENT_METHODS.careCredit]: {
      icon: careCreditIcon,
      id: 'reimbursement-care-credit',
      title: t('reimbursementSection.editModal.careCredit'),
    },
  }), [t]);

  const config = methodConfig[method];

  const onCardClick = useCallback(() => {
    onChange({ target: { value: method } });
  }, [method, onChange]);

  if (!config) {
    return null;
  }

  if (!newDesign) {
    return (
      <RadioButton
        blue
        checked={checked}
        className={legacyClassName}
        id={config.id}
        name="reimbursementMethod"
        onChange={onChange}
        value={method}
      >
        {config.title}
      </RadioButton>
    );
  }

  return (
    <button
      className={`${styles.card} ${
        config.recommended ? styles.cardRecommended : ''} ${
        checked ? styles.cardSelected : ''}`}
      onClick={onCardClick}
      type="button"
    >
      <span className={styles.radio} />

      <span className={styles.iconWrapper}>
        <img alt="" className={styles.icon} src={config.icon} />

        {config.iconBadge ? (
          <img alt="" className={styles.iconBadge} src={config.iconBadge} />
        ) : null}
      </span>

      <div className={styles.cardContent}>
        <span className={styles.cardTitle}>{config.title}</span>

        {config.content}
      </div>

      {config.recommended ? (
        <span className={styles.recommendedBadge}>
          {t('reimbursementSection.editModal.recommended')}
        </span>
      ) : null}
    </button>
  );
};

export { ReimbursementMethodOption };
